/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { parseUnits } from "viem"
import { useAccount, useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { formatEpochDate } from "@/components/lock"
import { Separator } from "@/components/separator"
import { TokenInput } from "@/components/token-input"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { CalendarIcon, CreateIcon, DollarIcon, WalletIcon } from "@/domains/icons"
import { createLockInteraction } from "@/domains/interactions"
import { CreateLockActionsAdapter } from "@/domains/interactions/create-lock/create-lock-actions-adapter"
import { dollars, formatCurrency } from "@/domains/library"
import { CreateLockSubItem } from "@/domains/navigation/subitems/create-lock-subitem"
import { LockSubItem } from "@/domains/navigation/subitems/lock-subitem"
import { EpochSlider } from "@/features/lock/src/epoch-slider"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useCreateLockLens } from "@/hooks/use-create-lock-lens"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useGovernanceTokenPrice } from "@/hooks/use-governance-token-price"
import { useVestedToken } from "@/hooks/use-vested-token"
import useEventEmitter from "@/hooks/useEventEmitter"
import { epochMath } from "@/pages/lock/[id]/extend-duration"

const veTokensPerDay = 1 / 182

const BREADCRUMB_ITEMS = [
  new LockSubItem(),
  new CreateLockSubItem(),
  // new ClassicLiquiditySubItem(),
]

export default observer(() => {
  const account = useAccount()
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const governanceToken = useGovernanceToken()
  const vestedToken = useVestedToken()
  const [amountToken0, setAmountToken0] = useState("0")
  const [numWeeks, setNumWeeks] = useState(26)
  const governanceTokenPrice = useGovernanceTokenPrice()
  const scaleApp = useScaleApplication()

  const updateToken0Amount = (newAmount: string) => {
    if (!newAmount) {
      setAmountToken0("0")
      return
    }
    setAmountToken0(newAmount)
  }

  const chainId = chainConfig.chainId

  // fuck I hate typescript sometimes, I've spent an hour trying to fix this type error, give up.
  const { createLockLens, refetchCreateLockLens } = useCreateLockLens({
    account: account?.address!,
    chainId,
    config,
    governanceToken,
    vestedToken,
  })

  const nextEpochMath = useMemo(() => epochMath(numWeeks), [numWeeks])
  const canCreateLock =
    parseUnits(amountToken0, governanceToken.decimals) > 0 &&
    numWeeks > 0 &&
    createLockLens.accountBalanceToken.wei > 0n

  const token0DollarValue = Number(amountToken0) * governanceTokenPrice

  const expectedVotingPower = useMemo(() => {
    if (!amountToken0) return { futurePower: 0 }
    const futureAmount = Number(amountToken0)
    const futurePower = veTokensPerDay * futureAmount * numWeeks * 7
    return { futurePower }
  }, [amountToken0, numWeeks])

  const onCreateLockClick = async () => {
    try {
      if (!account.address) {
        console.log("account", account)
        return
      }

      const onChainActions = new CreateLockActionsAdapter()

      const result = await createLockInteraction({
        account: account.address,
        chainId,
        config,
        amount: parseUnits(amountToken0, governanceToken.decimals), // need to be bigint value
        approvalMode: scaleApp?.Settings.approvalMode,
        secondsToExpire: nextEpochMath.secondsToExtend,
        formattedLockDate: formatEpochDate(nextEpochMath.desiredEpoch),
        contractRiskLevel: "immutable",
        createLockLens,
        governanceToken,
        vestedToken,
        emitter,
        eventBus: scaleApp.EventBus,
        onChainActions,
      })

      console.log("CreatePage→onCreateLockClick→result", result)
      void refetchCreateLockLens()
    } catch (error) {
      console.error("CreatePage→onCreateLockClick→error", error)
    }
  }

  const title = "Create veSCALE Lock"

  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title="Create veSCALE Lock" items={BREADCRUMB_ITEMS} />
        <div className="w-full text-t100">
          <Card.Container className="flex w-full max-w-xl flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
            <Card.Header>
              <Card.HeaderIconContainer>
                <CreateIcon size={24} className="text-primary-500" />
              </Card.HeaderIconContainer>

              <div className="flex w-full flex-col">
                <Card.HeaderTitle>Create {vestedToken.symbol} NFT</Card.HeaderTitle>
                <Card.HeaderDescription>
                  More tokens locked for longer = greater voting power = higher rewards.
                </Card.HeaderDescription>
              </div>
            </Card.Header>
            <Separator
              className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
          after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
          after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
              decorative
            />

            <div className="flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 p-4">
              <div className="flex flex-row items-center justify-between">
                <TokenInput.Value
                  value={amountToken0}
                  decimals={governanceToken.decimals}
                  symbol={governanceToken.symbol}
                  onValueChange={updateToken0Amount}
                  className="h-fit min-w-[180px] flex-grow p-0 "
                  placeholder="0"
                />
                <p className="text-t200">{governanceToken.symbol}</p>
              </div>

              {/* balances */}
              <div className="flex w-full flex-row items-center justify-between text-xs">
                <div className="flex items-center gap-1 text-sm text-t200">
                  <DollarIcon size={14} className="text-t200" />
                  <p>~{dollars(token0DollarValue)}</p>
                </div>
                <TokenInput.Balance
                  balance={createLockLens.accountBalanceToken.value}
                  label={<WalletIcon size={14} className="text-t200" />}
                  decimals={6}
                  // className={cn(errorToken0 && "text-warning")}
                />
              </div>

              <TokenInput.PercentageButtons className="grid w-full grid-cols-5 items-center gap-2">
                <TokenInput.PercentageButton
                  label="0%"
                  token={governanceToken}
                  multiplier={0}
                  hideTooltip
                  onClick={() => updateToken0Amount("0")}
                  tokenValue={createLockLens.accountBalanceToken.value}
                  variant="neutral"
                />
                <TokenInput.PercentageButton
                  label="25%"
                  token={governanceToken}
                  multiplier={0.25}
                  onClick={updateToken0Amount}
                  tokenValue={createLockLens.accountBalanceToken.value}
                  variant="neutral"
                />
                <TokenInput.PercentageButton
                  label="50%"
                  token={governanceToken}
                  multiplier={0.5}
                  onClick={updateToken0Amount}
                  tokenValue={createLockLens.accountBalanceToken.value}
                  variant="neutral"
                />
                <TokenInput.PercentageButton
                  label="75%"
                  token={governanceToken}
                  multiplier={0.75}
                  onClick={updateToken0Amount}
                  tokenValue={createLockLens.accountBalanceToken.value}
                  variant="neutral"
                />
                <TokenInput.PercentageButton
                  label="MAX"
                  token={governanceToken}
                  multiplier={1}
                  onClick={updateToken0Amount}
                  tokenValue={createLockLens.accountBalanceToken.value}
                  variant="neutral"
                />
              </TokenInput.PercentageButtons>
            </div>

            <div className="flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 px-4 py-2">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-1.5">
                  <CalendarIcon className="text-t200" size={18} /> {formatEpochDate(nextEpochMath.desiredEpoch)}
                </div>
                <div className="text-xs text-t200">Time Locked (weeks)</div>
              </div>
              <div className="min-h-[54px]">
                <EpochSlider
                  initialValue={numWeeks}
                  minWeeks={1}
                  onChange={(value: number) => {
                    setNumWeeks(value)
                  }}
                />
              </div>
            </div>

            <div className="flex justify-between gap-4 rounded-lg border border-p400-light bg-p400 px-4 py-2">
              <p className="text-t200">Your voting power will be: </p>
              <p>
                {formatCurrency(expectedVotingPower.futurePower, 2)} {vestedToken.symbol}
              </p>
            </div>

            <Button disabled={!canCreateLock} variant={"primary"} onClick={() => onCreateLockClick()}>
              Create Lock
            </Button>
          </Card.Container>
        </div>
      </PageContainer>
    </TooltipProvider>
  )
})
